import React, { useState, useEffect, useCallback } from "react";
import {
  Drawer, Typography, IconButton, Fab, Badge,
  CircularProgress, Tabs, Tab,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import CloseIcon from "@material-ui/icons/Close";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import { toast } from "react-toastify";

import { adminListTickets, adminGetCounts } from "../../services/supportTicketService";
import SupportTicketChat from "../SupportTicketChat";

const STATUS_STYLES = {
  aberto: { background: "#e0f2fe", color: "#0369a1" },
  em_atendimento: { background: "#f3e8ff", color: "#7c3aed" },
  aguardando_resposta: { background: "#fef9c3", color: "#92400e" },
  resolvido: { background: "#dcfce7", color: "#166534" },
  fechado: { background: "#f1f5f9", color: "#64748b" },
};

const STATUS_LABELS = {
  aberto: "Aberto",
  em_atendimento: "Em atendimento",
  aguardando_resposta: "Aguardando",
  resolvido: "Resolvido",
  fechado: "Fechado",
};

const TABS = [
  { value: "pendentes", label: "Pendentes" },
  { value: "todos", label: "Todos" },
  { value: "resolvido", label: "Resolvidos" },
  { value: "fechado", label: "Fechados" },
];

const useStyles = makeStyles((theme) => ({
  fab: {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 1200,
    width: 56,
    height: 56,
    boxShadow: "0 4px 20px rgba(0,0,0,0.18)",
    // Celular: subia por cima dos botoes do rodape das telas e ficava escondido
    // atras da barrinha do iPhone. Menor e mais alto.
    [theme.breakpoints.down("sm")]: {
      width: 48,
      height: 48,
      right: 14,
      bottom: "calc(max(12px, env(safe-area-inset-bottom)) + 72px)",
    },
  },
  drawerPaper: {
    width: 400,
    maxWidth: "100vw",
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  header: {
    background: theme.palette.primary.main,
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 10 },
  headerIcon: {
    width: 36, height: 36, borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  headerTitle: { color: "#fff", fontWeight: 700, fontSize: "1rem" },
  headerSubtitle: { color: "rgba(255,255,255,0.75)", fontSize: "0.72rem" },
  tabsRow: {
    background: "#fff",
    borderBottom: "1px solid #f1f5f9",
    flexShrink: 0,
    "& .MuiTabs-indicator": {
      backgroundColor: theme.palette.primary.main,
      height: 3,
    },
  },
  tab: {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.8rem",
    minHeight: 40,
    minWidth: 80,
    "&.Mui-selected": { color: theme.palette.primary.main, fontWeight: 700 },
  },
  tabBadge: {
    display: "inline-block",
    background: "#ef4444",
    color: "#fff",
    borderRadius: 10,
    fontSize: "0.6rem",
    fontWeight: 700,
    padding: "1px 5px",
    marginLeft: 4,
    verticalAlign: "middle",
  },
  body: {
    flex: 1,
    overflowY: "auto",
    background: "#f8fafc",
  },
  loadingBox: {
    display: "flex", justifyContent: "center", alignItems: "center", padding: "40px 0",
  },
  emptyBox: {
    textAlign: "center", padding: "48px 24px",
    color: "#94a3b8", fontSize: "0.85rem",
  },
  emptyIcon: { fontSize: 48, color: "#cbd5e1", marginBottom: 8 },
  sectionLabel: {
    padding: "10px 16px 4px",
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  ticketCard: {
    background: "#fff",
    borderRadius: 10,
    margin: "0 12px 8px",
    padding: "11px 14px",
    cursor: "pointer",
    border: "1px solid #f1f5f9",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    transition: "box-shadow 0.15s",
    "&:hover": { boxShadow: "0 2px 10px rgba(0,0,0,0.09)" },
  },
  ticketIcon: {
    width: 34, height: 34, borderRadius: "50%",
    background: "#f1f5f9",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
    color: theme.palette.primary.main,
  },
  ticketBody: { flex: 1, minWidth: 0 },
  ticketTitle: {
    fontWeight: 600, fontSize: "0.85rem", color: "#1e293b",
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    marginBottom: 3,
  },
  companyName: {
    fontSize: "0.72rem", color: "#64748b", marginBottom: 4,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
  },
  metaRow: { display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" },
  statusBadge: {
    padding: "1px 7px", borderRadius: 8,
    fontSize: "0.62rem", fontWeight: 700,
  },
  dateText: { fontSize: "0.66rem", color: "#94a3b8" },
}));

const formatDate = (d) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const PENDING_STATUSES = ["aberto", "aguardando_resposta"];

const SupportAdminWidget = () => {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("pendentes");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({});
  const [chatTicketId, setChatTicketId] = useState(null);

  const loadCounts = useCallback(() => {
    adminGetCounts()
      .then(({ data }) => setCounts(data))
      .catch(() => {});
  }, []);

  const loadTickets = useCallback(() => {
    setLoading(true);
    const status = activeTab === "pendentes" ? undefined : activeTab;
    adminListTickets(status === "todos" ? undefined : status)
      .then(({ data }) => {
        if (activeTab === "pendentes") {
          setTickets(data.filter((t) => PENDING_STATUSES.includes(t.status)));
        } else {
          setTickets(data);
        }
      })
      .catch(() => toast.error("Erro ao carregar chamados"))
      .finally(() => setLoading(false));
  }, [activeTab]);

  useEffect(() => {
    loadCounts();
    const interval = setInterval(loadCounts, 60000);
    return () => clearInterval(interval);
  }, [loadCounts]);

  useEffect(() => {
    if (open) loadTickets();
  }, [open, loadTickets]);

  const pendingCount = (counts.aberto || 0) + (counts.aguardando_resposta || 0);

  const grouped = activeTab === "pendentes"
    ? [
        { label: "Aguardando resposta", items: tickets.filter((t) => t.status === "aguardando_resposta") },
        { label: "Abertos", items: tickets.filter((t) => t.status === "aberto") },
      ].filter((g) => g.items.length > 0)
    : [{ label: null, items: tickets }];

  return (
    <>
      <Fab color="primary" className={classes.fab} onClick={() => setOpen(true)}>
        <Badge badgeContent={pendingCount} color="error" max={9}>
          <HeadsetMicIcon style={{ fontSize: 26 }} />
        </Badge>
      </Fab>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        classes={{ paper: classes.drawerPaper }}
        ModalProps={{ keepMounted: true }}
      >
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <div className={classes.headerIcon}>
              <HeadsetMicIcon style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div>
              <Typography className={classes.headerTitle}>Chamados de Suporte</Typography>
              <Typography className={classes.headerSubtitle}>
                {pendingCount > 0 ? `${pendingCount} aguardando atendimento` : "Nenhum pendente"}
              </Typography>
            </div>
          </div>
          <IconButton size="small" style={{ color: "#fff" }} onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* Tabs */}
        <div className={classes.tabsRow}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {TABS.map((tab) => {
              const count = tab.value === "pendentes"
                ? pendingCount
                : counts[tab.value] || 0;
              return (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  className={classes.tab}
                  label={
                    <span>
                      {tab.label}
                      {count > 0 && (
                        <span className={classes.tabBadge}>{count}</span>
                      )}
                    </span>
                  }
                />
              );
            })}
          </Tabs>
        </div>

        {/* Body */}
        <div className={classes.body}>
          {loading ? (
            <div className={classes.loadingBox}>
              <CircularProgress size={28} />
            </div>
          ) : tickets.length === 0 ? (
            <div className={classes.emptyBox}>
              <HeadsetMicIcon className={classes.emptyIcon} style={{ display: "block", margin: "0 auto 8px" }} />
              <Typography style={{ fontWeight: 600, color: "#64748b" }}>
                {activeTab === "pendentes" ? "Nenhum chamado pendente" : "Nenhum chamado"}
              </Typography>
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.label || "all"}>
                {group.label && (
                  <div className={classes.sectionLabel}>{group.label}</div>
                )}
                {group.items.map((t) => (
                  <div
                    key={t.id}
                    className={classes.ticketCard}
                    onClick={() => setChatTicketId(t.id)}
                  >
                    <div className={classes.ticketIcon}>
                      <ChatBubbleOutlineIcon style={{ fontSize: 16 }} />
                    </div>
                    <div className={classes.ticketBody}>
                      <div className={classes.ticketTitle}>{t.title}</div>
                      <div className={classes.companyName}>
                        {t.company?.name || `Empresa #${t.companyId}`}
                      </div>
                      <div className={classes.metaRow}>
                        <span
                          className={classes.statusBadge}
                          style={STATUS_STYLES[t.status] || {}}
                        >
                          {STATUS_LABELS[t.status] || t.status}
                        </span>
                        <span className={classes.dateText}>{formatDate(t.updatedAt)}</span>
                      </div>
                    </div>
                    <ChevronRightIcon style={{ color: "#cbd5e1", fontSize: 18, flexShrink: 0 }} />
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </Drawer>

      <SupportTicketChat
        open={Boolean(chatTicketId)}
        onClose={() => { setChatTicketId(null); loadTickets(); loadCounts(); }}
        ticketId={chatTicketId}
        isAdmin={true}
        onUpdated={() => { loadTickets(); loadCounts(); }}
      />
    </>
  );
};

export default SupportAdminWidget;
